"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Mail, ArrowLeft, CheckCircle2, Clock, Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "/api";

type Step = "email" | "sent";

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // SEC-004 (2026-10-17): the API no longer says whether the email has an
  // account (it used to return name / role / hospital — a staff-enumeration
  // leak). It always answers with the same generic message.
  const [sentTo, setSentTo] = useState("");
  const [sentMessage, setSentMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/users/forgot-password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setSentTo(email.trim().toLowerCase());
      setSentMessage(data.message || "");
      setStep("sent");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // Step 2: Verification link sent
  // =========================================================================
  if (step === "sent") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex flex-col items-center gap-3">
            <Image
              src="/logo-light.png"
              alt="OneMed EHR"
              width={200}
              height={80}
              priority
              className="dark:hidden"
            />
            <Image
              src="/logo-dark.png"
              alt="OneMed EHR"
              width={200}
              height={80}
              priority
              className="hidden dark:block"
            />
          </div>

          <Card className="shadow-lg border-border/50">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-4">
                {/* Success icon */}
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>

                <div className="space-y-1">
                  <h2 className="text-xl font-semibold">Check Your Email</h2>
                  <p className="text-sm text-muted-foreground">
                    {sentMessage ||
                      "If an account exists for this email, a password reset link has been sent to it."}
                  </p>
                </div>

                {/* Account info card */}
                <div className="w-full rounded-xl bg-muted/40 border p-4 text-left space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 flex-shrink-0 mt-0.5">
                      <Shield className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">Sent to</p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-all">{sentTo}</p>
                    </div>
                  </div>
                </div>

                {/* Spam notice — prominent */}
                <div className="w-full rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <Mail className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                        Can&apos;t find the email?
                      </p>
                      <p className="text-xs text-blue-700/80 dark:text-blue-400/70 mt-1 leading-relaxed">
                        Please check your <strong>Spam</strong> or <strong>Junk</strong> folder.
                        The email is sent from <strong>no-reply@onelearnai.com</strong> and may be
                        filtered by your email provider.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expiry notice */}
                <div className="w-full flex items-start gap-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3">
                  <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-400">
                      Link expires in 1 hour
                    </p>
                    <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                      The verification link will expire after 1 hour for security purposes.
                      Request a new one if it expires.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="w-full space-y-2 pt-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setStep("email");
                      setEmail("");
                      setSentTo("");
                      setSentMessage("");
                    }}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Try a different email
                  </Button>
                  <Link href="/login" className="block">
                    <Button variant="ghost" className="w-full text-muted-foreground">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} OneMedAI &middot; All rights reserved
          </p>
        </div>
      </div>
    );
  }

  // =========================================================================
  // Step 1: Enter email
  // =========================================================================
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <Image
            src="/logo-light.png"
            alt="OneMed EHR"
            width={200}
            height={80}
            priority
            className="dark:hidden"
          />
          <Image
            src="/logo-dark.png"
            alt="OneMed EHR"
            width={200}
            height={80}
            priority
            className="hidden dark:block"
          />
          <Badge variant="secondary" className="text-xs font-normal px-3">
            Electronic Health Record
          </Badge>
        </div>

        <Card className="shadow-lg border-border/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">Forgot Password</CardTitle>
            <CardDescription>
              Enter your registered email address and we&apos;ll send you a verification link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                  <span className="mt-0.5 shrink-0">!</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-9"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2.5 rounded-lg bg-muted/50 border px-4 py-3">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  A verification link will be sent to your email. The link will
                  expire in <strong>1 hour</strong> for security purposes.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || !email.trim()}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  "Send Verification Link"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                href="/login"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} OneMedAI &middot; All rights reserved
        </p>
      </div>
    </div>
  );
}
